import React from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import PresentToAllRoundedIcon from '@mui/icons-material/PresentToAllRounded'
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded'
import BoundVideoSurface from './BoundVideoSurface'

void React

const MEDIA_URL_PREFIX = 'https://media.aumhum.com/images/prod/'
const ROLE_LABELS = Object.freeze({
  mentor: 'Mentor',
  patient: 'Patient',
  participant: 'Participant',
  doctor: 'Doctor',
  user: 'User',
  screen: 'Screen',
  panelist: 'Panelist',
  auditor: 'Auditor',
  observer: 'Observer',
  support: 'Support',
})
const PROVIDER_SHORT_LABELS = Object.freeze({
  agora: 'AGR',
  '100ms': '100MS',
  'daily-co': 'DLY',
  'future-provider': 'RTC',
})

function getInitials(displayName) {
  if (typeof displayName !== 'string' || !displayName.trim()) {
    return '?'
  }

  const nameParts = displayName.trim().split(/\s+/).filter(Boolean)
  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 1).toUpperCase()
  }

  return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
}

function resolveProfilePictureUrl(profilePicId) {
  if (typeof profilePicId !== 'string' || !profilePicId.trim()) {
    return null
  }

  const normalizedProfilePicId = profilePicId.trim()
  if (
    normalizedProfilePicId.startsWith('http://') ||
    normalizedProfilePicId.startsWith('https://')
  ) {
    return normalizedProfilePicId
  }

  return `${MEDIA_URL_PREFIX}${normalizedProfilePicId}`
}

function resolveRoleLabel(participant = {}) {
  if (typeof participant?.roleLabel === 'string' && participant.roleLabel.trim()) {
    return participant.roleLabel.trim()
  }

  if (typeof participant?.role === 'string' && participant.role.trim()) {
    return ROLE_LABELS[participant.role.trim()] || 'Participant'
  }

  return 'Participant'
}

function resolveProviderLabel(participant = {}, debugContext = {}) {
  const providerId = participant?.transport?.providerId || debugContext?.providerId

  if (typeof providerId !== 'string' || !providerId.trim()) {
    return null
  }

  const normalizedProviderId = providerId.trim()
  const shortLabel = PROVIDER_SHORT_LABELS[normalizedProviderId]

  return shortLabel ? `${shortLabel} · ${normalizedProviderId}` : normalizedProviderId.toUpperCase()
}

function formatQualityProfile(profile) {
  if (!profile) {
    return null
  }

  const { width, height, frameRate, bitrateKbps } = profile

  if (!width || !height || !frameRate || !bitrateKbps) {
    return null
  }

  return `${width}x${height} · ${frameRate}fps · ${bitrateKbps}kbps`
}

function resolveTransportLabel(participant = {}) {
  const transport = participant?.transport || {}

  if (transport?.uid || transport?.rtcUid) {
    return `UID ${transport.uid || transport.rtcUid}`
  }

  if (transport?.peerId) {
    return `Peer ${transport.peerId}`
  }

  if (transport?.dailySessionId || transport?.sessionId) {
    return `Session ${transport.dailySessionId || transport.sessionId}`
  }

  return null
}

function buildDebugRows(participant = {}, debugContext = {}) {
  const roleLabel = resolveRoleLabel(participant)
  const providerLabel = resolveProviderLabel(participant, debugContext)
  const transportLabel = resolveTransportLabel(participant)
  const quality = debugContext?.videoQuality || null

  return [
    ['Provider', providerLabel],
    ['Phase', debugContext?.callPhase || null],
    ['Surface', debugContext?.sessionInterfaceId || null],
    ['Layout', debugContext?.layoutId || null],
    ['Rail', debugContext?.railStateId || null],
    ['Role', roleLabel],
    ['Local', participant?.isLocal === true ? 'Yes' : 'No'],
    ['Participant', participant?.participantId || participant?.id || null],
    ['Transport', transportLabel],
    ['State', participant?.connectionState || 'connected'],
    ['Audio', participant?.audioMuted ? 'Muted' : 'Live'],
    ['Video', participant?.videoMuted ? 'Off' : 'Live'],
    ['Share', participant?.screenSharing ? 'On' : 'Off'],
    [
      'Policy',
      quality ? `${String(quality.tier || 'sd').toUpperCase()} · ${quality.source || 'default'}` : null,
    ],
    ['Publish cap', formatQualityProfile(quality?.publishProfile)],
    ['Low stream', formatQualityProfile(quality?.lowStreamProfile)],
    ['Dual stream', quality ? (quality.enableDualStream ? 'On' : 'Off') : null],
    ['Render key', participant?.renderKey || null],
  ].filter(([, value]) => Boolean(value))
}

export default function ParticipantTile({
  participant,
  videoBinder,
  isPrimary = false,
  onClick = null,
  debugContext = null,
}) {
  const hasVideo = Boolean(participant?.videoHandle) && participant?.videoMuted !== true
  const displayName = participant?.displayName || 'Participant'
  const roleLabel = resolveRoleLabel(participant)
  const avatarUrl = React.useMemo(
    () => resolveProfilePictureUrl(participant?.profilePicId),
    [participant?.profilePicId],
  )
  const [avatarLoadFailed, setAvatarLoadFailed] = React.useState(false)
  const clickable = typeof onClick === 'function'
  const [debugOpen, setDebugOpen] = React.useState(false)
  const debugRows = React.useMemo(
    () => buildDebugRows(participant, debugContext),
    [debugContext, participant],
  )
  const ariaLabel = clickable
    ? `Focus ${participant?.isLocal ? 'you' : displayName}`
    : participant?.isLocal
      ? 'You'
      : displayName
  const avatarAlt = `${displayName} avatar`

  React.useEffect(() => {
    setAvatarLoadFailed(false)
  }, [avatarUrl])

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`shared-video-tile${isPrimary ? ' shared-video-tile--primary' : ''}${
        clickable ? ' shared-video-tile--interactive' : ''
      }${
        participant?.screenSharing ? ' shared-video-tile--screen-share' : ''
      }`}
      onClick={clickable ? () => onClick(participant) : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick(participant)
              }
            }
          : undefined
      }
      aria-label={ariaLabel}
    >
      {hasVideo ? (
        <BoundVideoSurface
          participant={participant}
          videoBinder={videoBinder}
          className="shared-video-surface"
          mirrorVideo={participant?.isLocal === true && participant?.screenSharing !== true}
        />
      ) : (
        <div className="shared-video-placeholder">
          {avatarUrl && !avatarLoadFailed ? (
            <img
              src={avatarUrl}
              alt={avatarAlt}
              className="shared-video-placeholder__image"
              onError={() => {
                setAvatarLoadFailed(true)
              }}
            />
          ) : (
            <div className="shared-video-placeholder__avatar">{getInitials(displayName)}</div>
          )}
        </div>
      )}

      <div className="shared-video-tile__scrim" />
      <div className="shared-video-tile__meta">
        <div className="shared-video-tile__identity">
          {avatarUrl && !avatarLoadFailed && hasVideo ? (
            <img
              src={avatarUrl}
              alt={avatarAlt}
              className="shared-video-tile__identity-avatar"
              onError={() => {
                setAvatarLoadFailed(true)
              }}
            />
          ) : (
            <span className="shared-video-tile__identity-avatar shared-video-tile__identity-avatar--fallback">
              {getInitials(displayName)}
            </span>
          )}
          <div className="shared-video-tile__identity-main">
            <span className="shared-video-tile__name">
              {participant?.isLocal ? 'You' : displayName}
            </span>
            {participant?.role ? (
              <span className="shared-video-tile__role">{roleLabel}</span>
            ) : null}
          </div>
        </div>
        <div className="shared-video-tile__status">
          {debugRows.length ? (
            <button
              type="button"
              className={`shared-video-tile__debug-button${
                debugOpen ? ' shared-video-tile__debug-button--active' : ''
              }`}
              aria-label={`${debugOpen ? 'Hide' : 'Show'} debug info for ${
                participant?.isLocal ? 'you' : displayName
              }`}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setDebugOpen((previousValue) => !previousValue)
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </button>
          ) : null}
          {clickable ? (
            <span className="shared-video-tile__focus-hint">Focus</span>
          ) : null}
          {participant?.screenSharing ? (
            <span className="shared-video-tile__status-badge">
              <PresentToAllRoundedIcon sx={{ fontSize: 14 }} />
            </span>
          ) : null}
          {participant?.audioMuted ? (
            <span className="shared-video-tile__status-badge shared-video-tile__status-badge--muted">
              <MicOffRoundedIcon sx={{ fontSize: 14 }} />
            </span>
          ) : null}
          {participant?.videoMuted ? (
            <span className="shared-video-tile__status-badge shared-video-tile__status-badge--muted">
              <VideocamOffRoundedIcon sx={{ fontSize: 14 }} />
            </span>
          ) : null}
        </div>
      </div>

      {debugOpen ? (
        <div
          className="shared-video-tile__debug-panel"
          role="dialog"
          aria-label={`Debug info for ${participant?.isLocal ? 'you' : displayName}`}
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <div className="shared-video-tile__debug-title">Debug</div>
          <div className="shared-video-tile__debug-grid">
            {debugRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <span className="shared-video-tile__debug-label">{label}</span>
                <span className="shared-video-tile__debug-value">{value}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

ParticipantTile.displayName = 'ParticipantTile'
